/**
 * HighlightCardSuggestions - ハイライトを入れるとよさそうなカードの候補。
 * 選ぶとカードの末尾にハイライトを引用として足す。新しいカードを作って入れることもできる
 */

import { Plus, StickyNote } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { appendHighlightToMemo, getRelated, type RelatedItem } from '@/api/services/related'
import { Spinner } from '@/components/ui/spinner'
import { apiErrorMessage } from '@/lib/api-error'
import { memoPath } from '@/routes/paths'
import { useMemoStore } from '@/store/useMemoStore'

type Props = {
  highlightId: number
}

export const HighlightCardSuggestions = ({ highlightId }: Props) => {
  const navigate = useNavigate()
  const [cards, setCards] = useState<RelatedItem[] | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    getRelated('highlight', highlightId)
      .then((res) => {
        if (!cancelled) setCards(res.items.filter((i) => i.kind === 'card'))
      })
      .catch(() => {
        if (!cancelled) setCards([])
      })
    return () => {
      cancelled = true
    }
  }, [highlightId])

  const addTo = async (card: RelatedItem) => {
    setBusy(true)
    try {
      await appendHighlightToMemo(card.ref_id, highlightId)
      toast.success(`カード「${card.title}」に追加しました`)
      setCards((prev) => prev?.filter((c) => c.ref_id !== card.ref_id) ?? null)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'カードに追加できませんでした'))
    } finally {
      setBusy(false)
    }
  }

  const createCard = async () => {
    setBusy(true)
    try {
      const memo = await useMemoStore.getState().addMemo('', true)
      if (!memo) throw new Error('カードを作れませんでした')
      await appendHighlightToMemo(memo.id, highlightId)
      navigate(memoPath(memo.id))
    } catch (err) {
      toast.error(apiErrorMessage(err, 'カードを作れませんでした'))
      setBusy(false)
    }
  }

  return (
    <div className="mt-1 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
      {cards === null ? (
        <div className="flex justify-center py-2">
          <Spinner size={16} />
        </div>
      ) : (
        <ul className="space-y-1">
          {cards.length === 0 && <li className="text-muted-foreground">近いカードはありません</li>}
          {cards.map((card) => (
            <li key={card.ref_id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => void addTo(card)}
                className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-accent disabled:opacity-50"
              >
                <StickyNote className="size-3.5 shrink-0 text-amber-500" />
                <span className="min-w-0 flex-1 truncate">{card.title}</span>
                <span className="shrink-0 text-muted-foreground">{Math.round(card.similarity * 100)}% · 追加</span>
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              disabled={busy}
              onClick={() => void createCard()}
              className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              <Plus className="size-3.5 shrink-0" />
              新しいカードを作って入れる
            </button>
          </li>
        </ul>
      )}
    </div>
  )
}
